import React from 'react';
// src/pages/Landing.tsx

import { Link } from 'react-router-dom';
import { Shield, ArrowRight, CheckCircle2, X, Star, Users } from 'lucide-react';
import { Navbar } from '../components/Navbar';
import { TrustBadges } from '../components/ui/TrustBadges';
// ─── Quiénes usan Agreexa ─────────────────────────────────────────────────────
const WHO_USES = [{
  emoji: '📚',
  label: 'Profesores particulares'
}, {
  emoji: '🏗️',
  label: 'Albañiles y constructores'
}, {
  emoji: '⚡',
  label: 'Electricistas'
}, {
  emoji: '🔧',
  label: 'Plomeros'
}, {
  emoji: '💪',
  label: 'Entrenadores personales'
}, {
  emoji: '🌿',
  label: 'Jardineros'
}, {
  emoji: '🎨',
  label: 'Pintores'
}, {
  emoji: '⚖️',
  label: 'Abogados'
}, {
  emoji: '🧮',
  label: 'Contadores'
}, {
  emoji: '💻',
  label: 'Programadores'
}, {
  emoji: '🍎',
  label: 'Nutricionistas'
}, {
  emoji: '🧠',
  label: 'Psicólogos'
}, {
  emoji: '🏠',
  label: 'Servicios de limpieza'
}, {
  emoji: '🚚',
  label: 'Fletes y mudanzas'
}, {
  emoji: '✨',
  label: 'Cualquier servicio'
}];
const PROBLEM_CASES = [{
  emoji: '📚',
  who: 'Profesora de inglés',
  problem: 'Da la clase y el alumno dice que la reprogramará. Nunca paga.',
  pain: '1 hora de trabajo perdida'
}, {
  emoji: '🔧',
  who: 'Plomero',
  problem: 'Termina el trabajo. El cliente dice que no tiene efectivo ahora.',
  pain: 'Materiales + mano de obra sin cobrar'
}, {
  emoji: '🏗️',
  who: 'Albañil',
  problem: 'Completa la primera etapa de la obra. El cliente desaparece.',
  pain: 'Semanas de trabajo sin pago'
}];
const COMPARE = {
  without: ['Riesgo de impago total', 'Acuerdos informales por WhatsApp', 'Sin evidencia si hay conflicto', 'Pérdida de tiempo y dinero', 'Tensión en la relación comercial'],
  with: ['El cliente paga antes de recibir', 'Acuerdo formal con código único', 'Evidencia registrada del resultado', 'Si prestás el servicio, cobrás', 'Relación profesional y confiable']
};
const STATS = [{
  value: 3847,
  label: 'Acuerdos protegidos',
  suffix: ''
}, {
  value: 520000,
  label: 'Protegido en acuerdos',
  prefix: '$',
  suffix: ''
}, {
  value: 98.4,
  label: 'Tasa de éxito',
  suffix: '%',
  decimals: 1
}];
const HOW_IT_WORKS = [{
  n: '01',
  title: 'Creás el acuerdo',
  desc: 'Describís el servicio, el monto y los datos de tu cliente. Dos minutos.'
}, {
  n: '02',
  title: 'Compartís el enlace',
  desc: 'Tu cliente recibe un enlace único. Ve el acuerdo completo antes de pagar.'
}, {
  n: '03',
  title: 'El cliente paga',
  desc: 'Los fondos quedan protegidos. Ninguna de las dos partes puede perder.'
}, {
  n: '04',
  title: 'Prestás el servicio',
  desc: 'Dás la clase, terminás la obra, entregás el trabajo. Como acordaron.'
}, {
  n: '05',
  title: 'Cobrás',
  desc: 'El cliente confirma y los fondos se liberan automáticamente a tu cuenta.'
}];
export function Landing() {
  return <div className="min-h-screen bg-white" data-id="element-406">
      <Navbar data-id="element-407" />

      {/* ── Hero ─────────────────────────────────────────────────────────── */}
      <section className="relative overflow-hidden" data-id="element-408">
        <div className="absolute inset-0 bg-gradient-to-br from-gray-50 via-white to-blue-50/30 pointer-events-none" data-id="element-409" />

        <div className="relative max-w-4xl mx-auto px-4 sm:px-6 pt-20 pb-24 text-center" data-id="element-410">
          {/* Pill */}
          <div className="inline-flex items-center gap-2 bg-gray-900 text-white px-4 py-1.5 rounded-full text-xs font-medium mb-8" data-id="element-411">
            <Shield className="w-3.5 h-3.5 text-blue-400" data-id="element-412" />
            Infraestructura de acuerdos seguros
          </div>

          {/* Headline */}
          <h1 className="text-5xl sm:text-7xl font-black text-gray-900 leading-[1.02] tracking-tight mb-6" data-id="element-413">
            Prestás el servicio.
            <br data-id="element-414" />
            <span className="text-blue-600" data-id="element-415">Cobrás.</span>
            <br data-id="element-416" />
            <span className="text-gray-400 text-4xl sm:text-5xl font-bold" data-id="element-417">
              Siempre.
            </span>
          </h1>

          <p className="text-lg sm:text-xl text-gray-400 max-w-2xl mx-auto leading-relaxed mb-4" data-id="element-418">
            Agreexa protege acuerdos comerciales entre personas. No importa si
            sos profesora de inglés, plomero, contador o entrenador personal.
          </p>
          <p className="text-base text-gray-500 max-w-xl mx-auto mb-10 font-medium" data-id="element-419">
            Si acordaron un servicio y vos lo cumplís,{' '}
            <span className="text-gray-900" data-id="element-420">el pago está garantizado.</span>
          </p>

          {/* CTAs */}
          <div className="flex flex-col sm:flex-row items-center justify-center gap-3 mb-10" data-id="element-421">
            <Link to="/create" data-id="element-422">
              <button className="inline-flex items-center gap-2 bg-gray-900 text-white px-8 py-4 rounded-2xl font-bold text-base hover:bg-gray-700 transition-all active:scale-[0.98] shadow-lg shadow-gray-900/10" data-id="element-423">
                Crear acuerdo gratis
                <ArrowRight className="w-5 h-5" data-id="element-424" />
              </button>
            </Link>
            <Link to="/dashboard" data-id="element-425">
              <button className="inline-flex items-center gap-2 bg-white text-gray-700 px-8 py-4 rounded-2xl font-medium text-base border border-gray-200 hover:bg-gray-50 transition-all" data-id="element-426">
                Ver mis acuerdos
              </button>
            </Link>
          </div>

          <TrustBadges size="sm" data-id="element-427" />
        </div>
      </section>

      {/* ── El problema real ─────────────────────────────────────────────── */}
      <section className="py-20 px-4 sm:px-6 bg-gray-50" data-id="element-428">
        <div className="max-w-4xl mx-auto" data-id="element-429">
          <div className="text-center mb-12" data-id="element-430">
            <p className="text-xs font-semibold text-gray-400 uppercase tracking-widest mb-3" data-id="element-431">
              El problema que resolvemos
            </p>
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4" data-id="element-432">
              Todos corremos el mismo riesgo.
            </h2>
            <p className="text-gray-400 text-lg max-w-2xl mx-auto" data-id="element-433">
              El prestador tiene miedo de no cobrar. El cliente tiene miedo de
              no recibir. Agreexa elimina el miedo de ambos lados.
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4" data-id="element-434">
            {PROBLEM_CASES.map((c, i) => <div key={i} className="bg-white rounded-2xl p-5 border border-gray-100" data-id="element-435">
                <span className="text-3xl mb-3 block" data-id="element-436">{c.emoji}</span>
                <p className="font-bold text-gray-900 text-sm mb-2" data-id="element-437">{c.who}</p>
                <p className="text-sm text-gray-500 leading-relaxed mb-3" data-id="element-438">
                  {c.problem}
                </p>
                <div className="flex items-center gap-1.5 bg-red-50 rounded-lg px-2.5 py-1.5" data-id="element-439">
                  <X className="w-3.5 h-3.5 text-red-400 flex-shrink-0" data-id="element-440" />
                  <span className="text-xs text-red-600 font-medium" data-id="element-441">
                    {c.pain}
                  </span>
                </div>
              </div>)}
          </div>
        </div>
      </section>

      {/* ── Quién lo usa ─────────────────────────────────────────────────── */}
      <section className="py-20 px-4 sm:px-6" data-id="element-442">
        <div className="max-w-4xl mx-auto" data-id="element-443">
          <div className="text-center mb-10" data-id="element-444">
            <h2 className="text-3xl font-bold text-gray-900 mb-3" data-id="element-445">
              Para cualquier servicio.
            </h2>
            <p className="text-gray-400" data-id="element-446">
              Si cobrás por tu tiempo o tu trabajo, Agreexa es para vos.
            </p>
          </div>

          <div className="flex flex-wrap justify-center gap-3" data-id="element-447">
            {WHO_USES.map((item, i) => <div key={i} className={`flex items-center gap-2 px-4 py-2.5 rounded-full border text-sm font-medium transition-all ${item.label === 'Cualquier servicio' ? 'bg-gray-900 text-white border-gray-900' : 'bg-white text-gray-600 border-gray-200 hover:border-gray-300 hover:bg-gray-50'}`} data-id="element-448">
                <span data-id="element-449">{item.emoji}</span>
                {item.label}
              </div>)}
          </div>

          <p className="text-center text-sm text-gray-400 mt-6" data-id="element-450">
            ¿No ves tu rubro? No importa.{' '}
            <Link to="/create" className="text-blue-600 font-medium hover:underline" data-id="element-451">
              Agreexa funciona para cualquier acuerdo comercial.
            </Link>
          </p>
        </div>
      </section>

      {/* ── Stats ────────────────────────────────────────────────────────── */}
      <section className="py-16 px-4 bg-gray-900" data-id="element-452">
        <div className="max-w-3xl mx-auto" data-id="element-453">
          <p className="text-center text-xs font-medium text-gray-500 uppercase tracking-widest mb-10" data-id="element-454">
            Agreexa en números
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-8 text-center" data-id="element-455">
            {STATS.map((s, i) => <div key={i} data-id="element-456">
                <p className="text-4xl font-black text-white mb-1" data-id="element-457">
                  {s.prefix}
                  {s.value.toLocaleString('es-AR')}
                  {s.suffix}
                </p>
                <p className="text-sm text-gray-500" data-id="element-458">{s.label}</p>
              </div>)}
          </div>
        </div>
      </section>

      {/* ── Cómo funciona ────────────────────────────────────────────────── */}
      <section className="py-24 px-4 sm:px-6" data-id="element-459">
        <div className="max-w-4xl mx-auto" data-id="element-460">
          <div className="text-center mb-16" data-id="element-461">
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4" data-id="element-462">
              Cinco pasos. Sin complicaciones.
            </h2>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-5 gap-6" data-id="element-463">
            {HOW_IT_WORKS.map((step, i) => <div key={i} className="text-center" data-id="element-464">
                <div className="text-4xl font-black text-gray-100 mb-3 leading-none" data-id="element-465">
                  {step.n}
                </div>
                <h3 className="font-bold text-gray-900 text-sm mb-1" data-id="element-466">
                  {step.title}
                </h3>
                <p className="text-xs text-gray-400 leading-relaxed" data-id="element-467">
                  {step.desc}
                </p>
              </div>)}
          </div>
        </div>
      </section>

      {/* ── Comparación ──────────────────────────────────────────────────── */}
      <section className="py-24 px-4 sm:px-6 bg-gray-50" data-id="element-468">
        <div className="max-w-3xl mx-auto" data-id="element-469">
          <div className="text-center mb-12" data-id="element-470">
            <h2 className="text-3xl font-bold text-gray-900 mb-4" data-id="element-471">
              ¿Por qué Agreexa?
            </h2>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-id="element-472">
            <div className="bg-white rounded-2xl p-6 border border-gray-100" data-id="element-473">
              <div className="flex items-center gap-2 mb-4" data-id="element-474">
                <div className="w-7 h-7 bg-red-50 rounded-lg flex items-center justify-center" data-id="element-475">
                  <X className="w-3.5 h-3.5 text-red-500" data-id="element-476" />
                </div>
                <h3 className="font-semibold text-gray-900" data-id="element-477">Sin Agreexa</h3>
              </div>
              <ul className="space-y-2.5" data-id="element-478">
                {COMPARE.without.map((item, i) => <li key={i} className="flex items-start gap-2 text-sm text-gray-500" data-id="element-479">
                    <X className="w-4 h-4 text-red-300 flex-shrink-0 mt-0.5" data-id="element-480" />
                    {item}
                  </li>)}
              </ul>
            </div>

            <div className="bg-gray-900 rounded-2xl p-6" data-id="element-481">
              <div className="flex items-center gap-2 mb-4" data-id="element-482">
                <div className="w-7 h-7 bg-blue-600 rounded-lg flex items-center justify-center" data-id="element-483">
                  <Shield className="w-3.5 h-3.5 text-white" data-id="element-484" />
                </div>
                <h3 className="font-semibold text-white" data-id="element-485">Con Agreexa™</h3>
              </div>
              <ul className="space-y-2.5" data-id="element-486">
                {COMPARE.with.map((item, i) => <li key={i} className="flex items-start gap-2 text-sm text-gray-300" data-id="element-487">
                    <CheckCircle2 className="w-4 h-4 text-green-400 flex-shrink-0 mt-0.5" data-id="element-488" />
                    {item}
                  </li>)}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* ── CTA final ────────────────────────────────────────────────────── */}
      <section className="py-24 px-4 bg-white" data-id="element-489">
        <div className="max-w-2xl mx-auto text-center" data-id="element-490">
          <div className="inline-flex items-center gap-1.5 bg-blue-50 text-blue-700 px-3 py-1.5 rounded-full text-xs font-medium mb-6" data-id="element-491">
            <Star className="w-3.5 h-3.5" data-id="element-492" />
            0% comisión durante el lanzamiento
          </div>
          <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4" data-id="element-493">
            Tu próximo acuerdo,
            <br data-id="element-494" />
            protegido desde el primer momento.
          </h2>
          <p className="text-gray-400 mb-8" data-id="element-495">
            Sin registro. Sin suscripción. Solo creás el acuerdo y compartís el
            enlace.
          </p>
          <Link to="/create" data-id="element-496">
            <button className="inline-flex items-center gap-2 bg-gray-900 text-white px-8 py-4 rounded-2xl font-bold text-base hover:bg-gray-700 transition-all shadow-lg shadow-gray-900/10" data-id="element-497">
              Crear mi primer acuerdo
              <ArrowRight className="w-5 h-5" data-id="element-498" />
            </button>
          </Link>
          <p className="text-xs text-gray-400 mt-4 flex items-center justify-center gap-1" data-id="element-499">
            <Users className="w-3.5 h-3.5" data-id="element-500" />
            Usado por profesores, albañiles, contadores y más en toda
            Latinoamérica
          </p>
        </div>
      </section>

      {/* ── Footer ───────────────────────────────────────────────────────── */}
      <footer className="border-t border-gray-100 py-8 px-4" data-id="element-501">
        <div className="max-w-6xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-4" data-id="element-502">
          <div className="flex items-center gap-2" data-id="element-503">
            <div className="w-7 h-7 bg-gray-900 rounded-lg flex items-center justify-center" data-id="element-504">
              <Shield className="w-3.5 h-3.5 text-white" data-id="element-505" />
            </div>
            <span className="font-bold text-gray-900" data-id="element-506">
              Agreexa<span className="text-blue-600" data-id="element-507">™</span>
            </span>
          </div>
          <p className="text-xs text-gray-400" data-id="element-508">
            © {new Date().getFullYear()} Agreexa™ · Acuerdos seguros para
            cualquier servicio
          </p>
          <div className="flex items-center gap-4 text-xs text-gray-400" data-id="element-509">
            <Link to="/legal" className="hover:text-gray-700" data-id="element-510">
              Términos
            </Link>
            <Link to="/contact" className="hover:text-gray-700" data-id="element-511">
              Soporte
            </Link>
          </div>
        </div>
      </footer>
    </div>;
}